import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useMemo, useState} from 'react';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {TeamAvatar} from '../../components/common/avatars/teamAvatar';
import {BoxedIcon} from '../../components/common/boxedIcon';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {getTeamsUserIsAdminOf} from '../../store/data/teams/teamsSelectors';
import {SettingsResourceDialogListItem} from '../common/dialog/settingsResourceDialogListItem';
import {SettingsResourceListDialog} from '../common/dialog/settingsResourceListDialog';
import {NamespaceSelectResourceTypesEnum} from './settingsHomeTypes';

/*
 * Props.
 */

interface SettingsHomeNamespaceSelectDialogProps {
  resourceType: NamespaceSelectResourceTypesEnum;
  onTeamSelect: HandlerOf<string>;
  onRequestClose: Handler;
  isCreatingResource: boolean;
  shouldShowOnlyTeams: boolean;
}

/*
 * Component.
 */

const resourceTypesWithIndividual = [
  NamespaceSelectResourceTypesEnum.SIGNATURES,
  NamespaceSelectResourceTypesEnum.INBOXES,
  NamespaceSelectResourceTypesEnum.ANSWERS,
  NamespaceSelectResourceTypesEnum.TAGS,
  NamespaceSelectResourceTypesEnum.RULES,
];

export const SettingsHomeNamespaceSelectDialog: FC<SettingsHomeNamespaceSelectDialogProps> = (props) => {
  const theme = useUserTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const flatTeams = useAppSelector(getTeamsUserIsAdminOf);
  const userNamespace = useAppSelector(getUserNamespace);
  const teamsMap = _(flatTeams)
    .keyBy((t) => t.namespace)
    .value();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const needsIndividualItem = useMemo(
    () => resourceTypesWithIndividual.includes(props.resourceType) && !props.shouldShowOnlyTeams,
    [props.resourceType, props.shouldShowOnlyTeams],
  );
  const needsCompanyItem = useMemo(
    () => props.resourceType === NamespaceSelectResourceTypesEnum.RULES && isGlobalAdmin,
    [isGlobalAdmin, props.resourceType],
  );
  const teamNamespaces = useMemo(() => {
    if (searchQuery) {
      return flatTeams
        .filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map((t) => t.namespace);
    }

    const namespaces = flatTeams.map((t) => t.namespace);

    return _.compact([needsIndividualItem && userNamespace, needsCompanyItem && 'global', ...namespaces]);
  }, [flatTeams, needsCompanyItem, needsIndividualItem, searchQuery, userNamespace]);

  return (
    <SettingsResourceListDialog
      title={renderTitle(props.isCreatingResource, props.resourceType)}
      itemIds={teamNamespaces}
      onRequestClose={props.onRequestClose}
      searchQuery={searchQuery}
      onChange={(newQuery) => setSearchQuery(newQuery)}
      emptyLabel={<Trans id="homenamespaceselect-noteams">No teams</Trans>}
      renderItem={(namespace) => {
        const team = teamsMap[namespace];

        if (!team) {
          if (isTeammateNamespace(namespace)) {
            return (
              <SettingsResourceDialogListItem
                onClick={() => {
                  props.onTeamSelect(namespace);
                  props.onRequestClose();
                }}
                iconPart={
                  <BoxedIcon
                    name="lockSmall"
                    backgroundColor={theme.greys.shade30}
                    color={theme.greys.shade70}
                    size={VisualSizesEnum.SMALL}
                  />
                }
                title={<Trans id="homenamespaceselect-individual">Individual</Trans>}
                isSelected={false}
              />
            );
          }

          if (namespace === 'global') {
            return (
              <SettingsResourceDialogListItem
                onClick={() => {
                  props.onTeamSelect(namespace);
                  props.onRequestClose();
                }}
                iconPart={
                  <BoxedIcon
                    name="buildingFilledSmall"
                    backgroundColor={theme.greys.shade30}
                    color={theme.greys.shade70}
                    size={VisualSizesEnum.SMALL}
                  />
                }
                title={<Trans id="homenamespaceselect-company">Company</Trans>}
                isSelected={false}
              />
            );
          }
        }

        return (
          <SettingsResourceDialogListItem
            onClick={() => {
              props.onTeamSelect(team.namespace);
              props.onRequestClose();
            }}
            iconPart={<TeamAvatar size={32} team={team} />}
            title={team.name}
            isSelected={false}
          />
        );
      }}
    />
  );
};

const renderTitle = (isCreatingResource: boolean, resource: NamespaceSelectResourceTypesEnum) => {
  if (resource === NamespaceSelectResourceTypesEnum.RULES) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createrule">Create a rule in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seerules">See all rules in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.LOAD_BALANCING) {
    return <Trans id="settings-namespaceselect-seeloadbalancings">See load balancing in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.INBOXES) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createinbox">Create an inbox in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seeinboxs">See all inboxes in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.SIGNATURES) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createsignature">Create a signature in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seesignatures">See all signatures in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.ANSWERS) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createanswer">Create a message template in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seeanswers">See all message templates in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.CUSTOM_VIEWS) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createview">Create a view in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seeviews">See all views in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.TAGS) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createtag">Create a tag in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seetags">See all tags in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.ROLES) {
    if (isCreatingResource) {
      return <Trans id="settings-namespaceselect-createrole">Create a role in</Trans>;
    }

    return <Trans id="settings-namespaceselect-seeroles">See all roles in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.SHIFTS) {
    return <Trans id="settings-namespaceselect-seeshifts">See all shifts in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.USER_PROVISIONING) {
    return <Trans id="settings-namespaceselect-userprovisioning">Configure user provisioning in</Trans>;
  }

  if (resource === NamespaceSelectResourceTypesEnum.ACTIVITY_EXPORT) {
    return <Trans id="settings-namespaceselect-activityexport">Export activity of teammates in</Trans>;
  }

  if (isCreatingResource) {
    return <Trans id="settings-namespaceselect-addteammate">Add a teammate in</Trans>;
  }

  return <Trans id="settings-namespaceselect-seeteammates">See all teammates in</Trans>;
};
